import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiColumns, FiFilter, FiDownload } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import Icon13 from "../../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg";
import { ContactSection } from "../contacts/ContactSection";

// Import shared components
import { ColumnFilterDropdown, RangeFilterDropdown, DateRangeFilterDropdown, useFilterPopupPortal } from '../common/filters';
import { ColumnVisibilityDropdown, ResetButton, SortIcon, sortIconDirection } from '../common';

// Import utilities
import { formatCurrency, formatDate, getByKey } from '../../utils';
import { loadXLSX } from '../../utils/xlsxLoader';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useUserPreference } from '../../hooks/useUserPreference';
import { USER_PREFERENCE_KEYS } from '../../constants/userPreferences';

const DEFAULT_COLUMN_ORDER = [
  'Project__r.Project_System_Wide_ID__c',
  'Opportunity_System_Wide_ID__c',
  'Project__r.Name',
  'dv_Division_New__c',
  'dv_Type__c',
  'Contract_With__r.Name',
  'Contract_With__r.BillingStreet',
  'Contract_With__r.BillingCity',
  'Contract_With__r.BillingState',
  'Contract_With__r.BillingPostalCode',
  'RevisedRevenueBudget',
  'Company__c',
  'Status',
  'BillingProgress',
  'created_at',
  'next_onsite',
  'Scope_Description__c',
  'Contact_Archived',
  'Email_Archived'
];

const DEFAULT_VISIBLE_COLUMNS = [
  'Project__r.Project_System_Wide_ID__c',
  'Opportunity_System_Wide_ID__c',
  'Project__r.Name',
  'dv_Division_New__c',
  'dv_Type__c',
  'Contact_Archived',
  'Email_Archived'
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) {
    return DEFAULT_COLUMN_ORDER;
  }

  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) {
      merged.push(key);
    }
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  return savedColumns.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) {
    return DEFAULT_PINNED_COLUMNS;
  }
  return savedPins.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

// Lightweight archive view that mirrors Project List columns and layout
// but does not load any data. Includes column visibility and reset.

export function ProjectArchive() {
  const { authFetch, isReady } = useAuthFetch();
  // Data + search/sort/filters
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('archiveSearchTerm') || '');
  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('archiveSortConfig');
    return saved ? JSON.parse(saved) : { key: 'Project__r.Project_System_Wide_ID__c', direction: 'asc' };
  });

  // Columns matched to the project-archive API (px widths — same pattern as Projects/Contacts)
  const originalColumns = useMemo(() => ([
    { key: 'Project__r.Project_System_Wide_ID__c', label: 'Project No.', width: 120 },
    { key: 'Opportunity_System_Wide_ID__c', label: 'Opportunity No.', width: 140 },
    { key: 'Project__r.Name', label: 'Project Name', width: 220 },
    { key: 'dv_Division_New__c', label: 'Division', width: 120, filterable: true, filterKey: 'division' },
    { key: 'dv_Type__c', label: 'Scope', width: 140, filterable: true, filterKey: 'scope' },
    { key: 'Contract_With__r.Name', label: 'Customer', width: 180 },
    { key: 'Contract_With__r.BillingStreet', label: 'Street', width: 180, filterable: true, filterKey: 'street' },
    { key: 'Contract_With__r.BillingCity', label: 'City', width: 140, filterable: true, filterKey: 'city' },
    { key: 'Contract_With__r.BillingState', label: 'State', width: 80, filterable: true, filterKey: 'state' },
    { key: 'Contract_With__r.BillingPostalCode', label: 'Zip Code', width: 100, filterable: true, filterKey: 'zip' },
    { key: 'RevisedRevenueBudget', label: 'Amount', width: 120, isRangeFilter: true },
    { key: 'Company__c', label: 'Branch', width: 100, filterable: true, filterKey: 'branch' },
    { key: 'Status', label: 'Status', width: 120, filterable: true, filterKey: 'status' },
    { key: 'BillingProgress', label: 'Billing Progress', width: 120, isRangeFilter: true },
    { key: 'created_at', label: 'Created On', width: 120, isDateFilter: true },
    { key: 'next_onsite', label: 'Most Recent On Site', width: 150, isDateFilter: true },
    { key: 'Scope_Description__c', label: 'Scope Description', width: 200 },
    { key: 'Contact_Archived', label: 'Contact Archived', width: 140, isActionColumn: true },
    { key: 'Email_Archived', label: 'Email Archived', width: 140, isActionColumn: true },
  ]), []);

  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ARCHIVE_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'archiveVisibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ARCHIVE_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'archive_columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.ARCHIVE_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'archive_pinnedColumns',
    }
  );

  const columns = useMemo(() => {
    return [...originalColumns].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.key);
      const bIdx = columnOrder.indexOf(b.key);
      return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
    });
  }, [originalColumns, columnOrder]);

  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) => visibleColumns.includes(column.key) && !pinnedVisibleKeys.includes(column.key)
  );
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const selectorRef = useRef(null);
  const columnButtonRef = useRef(null);
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });
  const tableScrollRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = localStorage.getItem('archiveColumnFilters');
    return saved ? JSON.parse(saved) : Object.fromEntries(columns.map(c => [c.key, '']));
  });

  // Dropdown filter state (like ManageProject)
  const [columnFilterValues, setColumnFilterValues] = useState(() => {
    const saved = localStorage.getItem('archiveColumnFilterValues');
    return saved ? JSON.parse(saved) : {
      division: [],
      scope: [],
      branch: [],
      status: [],
      street: [],
      city: [],
      state: [],
      zip: []
    };
  });
  const [amountRange, setAmountRange] = useState(() => {
    const saved = localStorage.getItem('archiveAmountRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });
  const [billingProgressRange, setBillingProgressRange] = useState(() => {
    const saved = localStorage.getItem('archiveBillingProgressRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });
  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('archiveDateRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });
  const [lastOnSiteRange, setLastOnSiteRange] = useState(() => {
    const saved = localStorage.getItem('archiveLastOnSiteRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });

  // Export state for Download Excel
  const [isExporting, setIsExporting] = useState(false);

  // Archived contacts popup state
  const [isArchivedContactsModalOpen, setIsArchivedContactsModalOpen] = useState(false);
  const [selectedProjectForArchivedContacts, setSelectedProjectForArchivedContacts] = useState(null);
  const [archivedContactsData, setArchivedContactsData] = useState([]);
  const [loadingArchivedContacts, setLoadingArchivedContacts] = useState(false);

  // Archived emails popup state
  const [isArchivedEmailsModalOpen, setIsArchivedEmailsModalOpen] = useState(false);
  const [selectedProjectForArchivedEmails, setSelectedProjectForArchivedEmails] = useState(null);
  const [selectedEmailForArchivedEmails, setSelectedEmailForArchivedEmails] = useState(null);
  const [archivedEmailsData, setArchivedEmailsData] = useState([]);
  const [loadingArchivedEmails, setLoadingArchivedEmails] = useState(false);

  // Scope Description popup state
  const [isScopeDescriptionOpen, setIsScopeDescriptionOpen] = useState(false);
  const [scopeDescriptionText, setScopeDescriptionText] = useState('');
  const [scopeDescriptionTitle, setScopeDescriptionTitle] = useState('');

  useEffect(() => localStorage.setItem('archiveSearchTerm', searchTerm), [searchTerm]);
  useEffect(() => localStorage.setItem('archiveSortConfig', JSON.stringify(sortConfig)), [sortConfig]);
  useEffect(() => localStorage.setItem('archiveColumnFilters', JSON.stringify(columnFilters)), [columnFilters]);
  useEffect(() => localStorage.setItem('archiveColumnFilterValues', JSON.stringify(columnFilterValues)), [columnFilterValues]);
  useEffect(() => localStorage.setItem('archiveAmountRange', JSON.stringify(amountRange)), [amountRange]);
  useEffect(() => localStorage.setItem('archiveBillingProgressRange', JSON.stringify(billingProgressRange)), [billingProgressRange]);
  useEffect(() => localStorage.setItem('archiveDateRange', JSON.stringify(dateRange)), [dateRange]);
  useEffect(() => localStorage.setItem('archiveLastOnSiteRange', JSON.stringify(lastOnSiteRange)), [lastOnSiteRange]);

  // Fetch archive records function
  const fetchArchive = async () => {
    if (!isReady) return;
    setLoading(true);
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/project-archive`);
      if (!res.ok) throw new Error('Failed to fetch project archive');
      const data = await res.json();
      setRecords(Array.isArray(data.records) ? data.records : []);
    } catch (err) {
      console.error(err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch archive records on component mount
  useEffect(() => {
    fetchArchive();
  }, [isReady]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) setIsSelectorOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleColumnSelectorClick = () => {
    setIsSelectorOpen((prev) => {
      const next = !prev;
      if (next && columnButtonRef.current) {
        const rect = columnButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 240; // ~w-60
        let left = rect.left + window.scrollX;
        let top = rect.bottom + window.scrollY + 4;
        if (left + dropdownWidth > window.innerWidth - 8) left = window.innerWidth - dropdownWidth - 8;
        if (left < 8) left = 8;
        setColumnSelectorPosition({ top, left });
      }
      return next;
    });
  };

  // ColumnVisibilityDropdown is now imported from '../common'

  // Filter dropdowns close via X / Clear / Apply (same as Projects) — no outside-click auto-close.

  // Filter dropdown components are now imported from '../common/filters'

  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues((prev) => ({
      ...prev,
      [columnName]: prev[columnName]?.includes(value)
        ? prev[columnName].filter((v) => v !== value)
        : [...(prev[columnName] || []), value],
    }));
  };

  const onSort = (key) => {
    // Disable sorting for action columns (Contact/Email Archived)
    if (key === 'Contact_Archived' || key === 'Email_Archived') return;
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // formatCurrency, formatDate, and getByKey are now imported from '../../utils'

  const filteredAndSorted = useMemo(() => {
    let items = [...records];
    // global search
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      items = items.filter(r => [
        r.Opportunity_System_Wide_ID__c || '',
        r.Project__r?.Project_System_Wide_ID__c || '',
        r.Project__r?.Name || '',
        r.dv_Division_New__c || '',
        r.dv_Type__c || '',
        r.Company__c || '',
        r.Project_Scope__c || ''
      ].some(v => v.toString().toLowerCase().includes(t)));
    }
    // dropdown filters
    items = items.filter(r => {
      if (columnFilterValues.division?.length && !columnFilterValues.division.includes(r.dv_Division_New__c)) return false;
      if (columnFilterValues.scope?.length && !columnFilterValues.scope.includes(r.dv_Type__c)) return false;
      if (columnFilterValues.branch?.length && !columnFilterValues.branch.includes(r.Company__c)) return false;
      if (columnFilterValues.status?.length && !columnFilterValues.status.includes(r.Status)) return false;
      if (columnFilterValues.street?.length && !columnFilterValues.street.includes(r.Contract_With__r?.BillingStreet)) return false;
      if (columnFilterValues.city?.length && !columnFilterValues.city.includes(r.Contract_With__r?.BillingCity)) return false;
      if (columnFilterValues.state?.length && !columnFilterValues.state.includes(r.Contract_With__r?.BillingState)) return false;
      if (columnFilterValues.zip?.length && !columnFilterValues.zip.includes(r.Contract_With__r?.BillingPostalCode)) return false;
      // header text filters for specific columns
      const headerFilters = [
        'Project__r.Project_System_Wide_ID__c',
        'Opportunity_System_Wide_ID__c',
        'Project__r.Name',
        'Contract_With__r.Name',
        'Scope_Description__c'
      ];
      for (const key of headerFilters) {
        const filterVal = columnFilters[key];
        if (filterVal) {
          const cell = getByKey(r, key) || '';
          if (!cell.toString().toLowerCase().includes(filterVal.toLowerCase())) return false;
        }
      }
      // amount range
      const amt = parseFloat(r.RevisedRevenueBudget) || 0;
      if (amountRange.min !== null && amt < amountRange.min) return false;
      if (amountRange.max !== null && amt > amountRange.max) return false;
      // billing progress range
      const bp = parseFloat(r.BillingProgress) || 0;
      if (billingProgressRange.min !== null && bp < billingProgressRange.min) return false;
      if (billingProgressRange.max !== null && bp > billingProgressRange.max) return false;
      // date range
      if (dateRange.start || dateRange.end) {
        const ts = new Date(r.created_at);
        if (dateRange.start && new Date(dateRange.start) > ts) return false;
        if (dateRange.end && new Date(dateRange.end) < ts) return false;
      }
      // last on site range
      if (lastOnSiteRange.start || lastOnSiteRange.end) {
        const ts = new Date(r.next_onsite);
        if (lastOnSiteRange.start && new Date(lastOnSiteRange.start) > ts) return false;
        if (lastOnSiteRange.end && new Date(lastOnSiteRange.end) < ts) return false;
      }
      return true;
    });
    // sort
    if (sortConfig?.key) {
      const { key, direction } = sortConfig;
      items.sort((a, b) => {
        let av = getByKey(a, key);
        let bv = getByKey(b, key);
        if (key === 'RevisedRevenueBudget' || key === 'BillingProgress') {
          av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
        } else if (key === 'created_at' || key === 'next_onsite') {
          av = new Date(av).getTime() || 0; bv = new Date(bv).getTime() || 0;
        } else {
          av = (av ?? '').toString().toLowerCase();
          bv = (bv ?? '').toString().toLowerCase();
        }
        if (av < bv) return direction === 'asc' ? -1 : 1;
        if (av > bv) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [records, searchTerm, columnFilters, sortConfig, columns, columnFilterValues, amountRange, billingProgressRange, dateRange, lastOnSiteRange]);

  const resetAll = async () => {
    try {
      localStorage.removeItem('archiveSearchTerm');
      localStorage.removeItem('archiveSortConfig');
      localStorage.removeItem('archiveVisibleColumns');
      localStorage.removeItem('archive_columnOrder');
      localStorage.removeItem('archive_pinnedColumns');
      localStorage.removeItem('archiveColumnFilters');
      localStorage.removeItem('archiveColumnFilterValues');
      localStorage.removeItem('archiveAmountRange');
      localStorage.removeItem('archiveBillingProgressRange');
      localStorage.removeItem('archiveDateRange');
      localStorage.removeItem('archiveLastOnSiteRange');
    } catch (e) {}

    setSearchTerm('');
    setSortConfig({ key: 'Project__r.Project_System_Wide_ID__c', direction: 'asc' });
    setColumnFilters(Object.fromEntries(columns.map(c => [c.key, ''])));
    setColumnFilterValues({ division: [], scope: [], branch: [], status: [], street: [], city: [], state: [], zip: [] });
    setAmountRange({ min: null, max: null });
    setBillingProgressRange({ min: null, max: null });
    setDateRange({ start: null, end: null });
    setLastOnSiteRange({ start: null, end: null });
    setActiveFilterColumn(null);

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    await Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    window.location.reload();
  };

  // Function to transform archived contacts data (similar to Contacts.jsx)
  const transformArchivedContactsData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return;

    // Initialize the structure with empty contacts arrays
    const sections = {
      customerContacts: {
        title: "Nettles Project Team",
        contacts: [
          { title: "Operations Manager", contacts: [] },
          { title: "Project Manager", contacts: [] },
          { title: "Project Accountant", contacts: [] },
          { title: "Submittals", contacts: [] },
          { title: "Salesperson", contacts: [] },
          { title: "Superintendent", contacts: [] }
        ]
      },
      gcCustomer: {
        title: "GC/Customer",
        contacts: [
          { title: "Project Manager", contacts: [] },
          { title: "Asst. Project Manager", contacts: [] },
          { title: "Superintendent", contacts: [] },
          { title: "Project Engineer", contacts: [] },
          { title: "Billing Contact", contacts: [] }
        ]
      },
      Vendor: {
        title: "Vendors",
        contacts: [
          { 
            title: "Vendors",
            contacts: [] 
          }
        ]
      },
      Design: {
        title: "Designer/Consultant",
        contacts: [
          { title: "Architect", contacts: [] },
          { title: "Engineer", contacts: [] },
          { title: "Inspector", contacts: [] },
          { title: "Consultant", contacts: [] }
        ]
      }
    };

    // Map all contacts to our structure
    apiData.forEach(contact => {
      const contactData = {
        id: contact.id,
        name: `${contact.FirstName} ${contact.LastName}`,
        title: contact.AccountName || '-',
        email: contact.Email || '-',
        phones: [
          contact.Phone || '',
          contact.MobilePhone || ''
        ],
        ...contact,
        hasChat: contact.Group === "Nettles Project Team"
      };

      // Map Group to our section keys
      const sectionMap = {
        "Nettles Project Team": "customerContacts",
        "GC/Customer": "gcCustomer",
        "Vendors": "Vendor",
        "Designer/Consultant": "Design"
      };

      const sectionKey = sectionMap[contact.Group];
      if (!sectionKey) return;

      if (contact.Group === "Vendors") {
        sections[sectionKey].contacts[0].contacts.push(contactData);
      } else {
        const section = sections[sectionKey].contacts.find(
          s => s.title === contact.ContactType
        );
        if (section) {
          section.contacts.push(contactData);
        }
      }
    });

    return sections;
  };

  // Fetch archived contacts from API
  const fetchArchivedContacts = async (opportunityUuid) => {
    if (!isReady) return;
    setLoadingArchivedContacts(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/archive/contacts/${opportunityUuid}`);
      if (!response.ok) {
        throw new Error('Failed to fetch archived contacts');
      }
      const data = await response.json();
      setArchivedContactsData(data.data || []);
    } catch (error) {
      console.error('Error fetching archived contacts:', error);
      setArchivedContactsData([]);
    } finally {
      setLoadingArchivedContacts(false);
    }
  };

  // Handle click on "Click here" button for archived contacts
  const handleArchivedContactsClick = (projectData) => {
    setSelectedProjectForArchivedContacts(projectData);
    setIsArchivedContactsModalOpen(true);
    const opportunityUuid = projectData?.Opportunity_System_Wide_ID__c;
    if (opportunityUuid) {
      fetchArchivedContacts(opportunityUuid);
    }
  };

  // Helper functions for email formatting (from ProjectEmails.jsx)
  const formatDateForEmail = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  const formatDateTimeForEmail = (dateString) => {
    const date = new Date(dateString);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    return {
      fullDateTime: date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone
      }),
      timeZone: timeZone.replace(/_/g, ' ')
    };
  };

  // Transform archived emails data (similar to ProjectEmails.jsx)
  const transformArchivedEmailsData = (emailsData) => {
    if (!emailsData || !Array.isArray(emailsData)) return [];
    
    return emailsData.map(email => {
      const rawJson = email.raw_json || {};
      return {
        id: email.id,
        from: email.from_name || "Unknown",
        fromEmail: email.from_email || "",
        to: (rawJson.toRecipients || []).map(r => ({
          name: r.emailAddress?.name || "",
          email: r.emailAddress?.address || ""
        })),
        cc: (rawJson.ccRecipients || []).map(r => ({
          name: r.emailAddress?.name || "",
          email: r.emailAddress?.address || ""
        })),
        bcc: (rawJson.bccRecipients || []).map(r => ({
          name: r.emailAddress?.name || "",
          email: r.emailAddress?.address || ""
        })),
        time: email.received_at,
        subject: rawJson.subject || "No Subject",
        bodyPreview: rawJson.bodyPreview || "",
        body: rawJson.body?.content || "",
        folder: email.folder || "Inbox",
        isSelected: false,
      };
    });
  };

  // Fetch archived emails from API
  const fetchArchivedEmails = async (opportunityUuid) => {
    if (!isReady) return;
    setLoadingArchivedEmails(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_BASEURL}/api/archive/emails/${opportunityUuid}`);
      if (!response.ok) {
        throw new Error('Failed to fetch archived emails');
      }
      const data = await response.json();
      setArchivedEmailsData(data.data || []);
    } catch (error) {
      console.error('Error fetching archived emails:', error);
      setArchivedEmailsData([]);
    } finally {
      setLoadingArchivedEmails(false);
    }
  };

  // Handle click on "Click here" button for archived emails
  const handleArchivedEmailsClick = (projectData) => {
    setSelectedProjectForArchivedEmails(projectData);
    setIsArchivedEmailsModalOpen(true);
    const opportunityUuid = projectData?.Opportunity_System_Wide_ID__c;
    if (opportunityUuid) {
      fetchArchivedEmails(opportunityUuid);
    }
  };

  const toggleColumn = (key) => {
    const currentScroll = window.scrollY;
    const isHiding = visibleColumns.includes(key);
    const newColumns = isHiding
      ? visibleColumns.filter((k) => k !== key)
      : [...visibleColumns, key];
    setVisibleColumns(newColumns);
    saveVisibleColumns(newColumns);

    if (isHiding && pinnedColumns.includes(key)) {
      const nextPins = pinnedColumns.filter((k) => k !== key);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: currentScroll });
    });
  };

  const handleTogglePin = (columnKey) => {
    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }

    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }

    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  // Helper to produce display value for a given row + column (used by Excel export)
  const getColumnDisplayValue = (row, column) => {
    if (!row || !column) return '-';

    const key = column.key;

    // Basic mappings for special keys
    if (key === 'dv_Type__c') {
      return row.dv_Type__c || '-';
    }
    if (key === 'dv_Division_New__c') {
      return row.dv_Division_New__c || '-';
    }
    if (key === 'Status') {
      return row.Status || '-';
    }

    // Nested keys handled via getByKey
    if (key.includes('Project__r.') || key.includes('Contract_With__r.') || key.includes('project_info.')) {
      const val = getByKey(row, key);
      return val !== undefined && val !== null && val !== '' ? val : '-';
    }

    if (key === 'RevisedRevenueBudget') {
      const raw = getByKey(row, key);
      if (raw === undefined || raw === null || raw === '') return '-';
      const n = Number(raw);
      if (Number.isNaN(n)) return '-';
      return formatCurrency(n);
    }

    if (key === 'BillingProgress') {
      const raw = getByKey(row, key);
      return raw ? `${raw}%` : '-';
    }

    if (key === 'created_at' || key === 'next_onsite') {
      const raw = getByKey(row, key);
      return raw ? formatDate(raw) : '-';
    }

    const direct = getByKey(row, key);
    return direct !== undefined && direct !== null && direct !== '' ? direct : '-';
  };

  // Download Excel handler (pattern from ManageProject)
  const handleExportToExcel = async () => {
    if (isExporting) return;

    const exportColumns = columns.filter(column => visibleColumns.includes(column.key));
    if (exportColumns.length === 0) {
      console.warn('No columns selected for export');
      return;
    }

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        console.error('XLSX library not available');
        return;
      }

      const headers = exportColumns.map(column => column.label);
      const exportData = filteredAndSorted; // same data as table
      const rows = exportData.map(row =>
        exportColumns.map(column => getColumnDisplayValue(row, column))
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Projects');
      XLSX.writeFile(workbook, 'project-archive.xlsx');
    } catch (error) {
      console.error('Failed to export project archive:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;

    setColumnOrder(prevOrder => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    saveColumnOrder();
  };

  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);

  const handleHeaderDragStart = (e, columnKey) => {
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey) return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;
    if (wasPinned) {
      savePinnedColumns();
    } else {
      saveColumnOrder();
    }
  };

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, pinnedColumnsWidth, loading, filteredAndSorted.length]);

  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img loading="lazy" src={Icon13} className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]" alt="" />
            <input
              type="search"
              placeholder="Search Project Archive"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </form>
          <div className="flex-grow max-md:hidden" />

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={selectorRef}
                isOpen={isSelectorOpen}
                onClose={() => setIsSelectorOpen(false)}
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumn}
                position={columnSelectorPosition}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                requiredKeys={[
                  'Project__r.Project_System_Wide_ID__c',
                  'Opportunity_System_Wide_ID__c',
                  'Project__r.Name',
                  'dv_Division_New__c',
                  'dv_Type__c'
                ]}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || filteredAndSorted.length === 0 || isExporting}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${
                loading || filteredAndSorted.length === 0 || isExporting ? 'cursor-not-allowed opacity-60' : ''
              }`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={resetAll} tooltip="Reset" />
          </div>
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
        <table
          className="projects-data-table"
          style={{
            width: `max(100%, ${tableMinWidth}px)`,
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <colgroup>
            {visibleTableColumns.map((column) => {
              const isPinned = pinnedVisibleKeys.includes(column.key);
              return (
                <col
                  key={column.key}
                  style={
                    isPinned
                      ? {
                          width: `${column.width}px`,
                          minWidth: `${column.width}px`,
                        }
                      : {
                          width: `${(column.width / tableMinWidth) * 100}%`,
                          minWidth: `${column.width}px`,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {visibleTableColumns.map((col) => {
                const isPinned = pinnedVisibleKeys.includes(col.key);
                return (
                  <th
                    key={col.key}
                    style={getColumnStyle(col, { isHeader: true })}
                    className={`group/header px-4 py-3 max-md:whitespace-normal cursor-grab active:cursor-grabbing transition-colors hover:bg-gray-100 ${
                      isPinned ? 'projects-col-pinned' : ''
                    } ${col.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleHeaderDragStart(e, col.key)}
                    onDragOver={(e) => handleHeaderDragOver(e, col.key)}
                    onDragEnd={handleHeaderDragEnd}
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className={`flex items-center justify-center text-sm font-medium text-gray-500 whitespace-nowrap ${col.isActionColumn ? '' : 'cursor-pointer'}`}
                        onClick={col.isActionColumn ? undefined : () => onSort(col.key)}
                      >
                        <span className="truncate" title={col.label}>{col.label}</span>
                        <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                          {col.isActionColumn ? null : (
                            <SortIcon direction={sortIconDirection(sortConfig, col.key)} />
                          )}
                        </span>
                        <button
                          type="button"
                          data-pin-toggle
                          title={isPinned ? 'Unpin column' : 'Pin column to left'}
                          aria-label={isPinned ? `Unpin ${col.label}` : `Pin ${col.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTogglePin(col.key);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          draggable={false}
                          className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                            isPinned
                              ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                          }`}
                        >
                          <span className="inline-flex">
                            {isPinned ? (
                              <MdPushPin className="h-3.5 w-3.5" />
                            ) : (
                              <MdOutlinePushPin className="h-3.5 w-3.5" />
                            )}
                          </span>
                        </button>
                      </div>
                      <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                        {col.filterable ? (
                          <div
                            className="relative"
                            ref={activeFilterColumn === col.filterKey ? filterDropdownRef : null}
                            style={getFilterWrapperStyle(activeFilterColumn === col.filterKey)}
                          >
                            <button
                              onClick={(e) => toggleFilterPopup(col.filterKey, e)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none text-center font-light flex items-center justify-between ${
                                columnFilterValues[col.filterKey]?.length>0 ? 'text-red-600 border-red-500' : 'text-zinc-500'
                              }`}
                            >
                              <span>
                                {columnFilterValues[col.filterKey]?.length>0 ? `${columnFilterValues[col.filterKey].length} selected` : 'Filter'}
                              </span>
                              <FiFilter className="ml-2" />
                            </button>
                            {activeFilterColumn === col.filterKey &&
                              renderFilterPopup(
                                <ColumnFilterDropdown
                                  isOpen={activeFilterColumn === col.filterKey}
                                  options={[...new Set(records.map(r => getByKey(r, col.key)).filter(Boolean))].sort()}
                                  selectedValues={columnFilterValues[col.filterKey] || []}
                                  onSelect={(value) => handleColumnFilterSelect(col.filterKey, value)}
                                  onClose={closeFilterPopup}
                                />
                              )}
                          </div>
                        ) : col.isRangeFilter ? (
                          <div
                            className="relative"
                            ref={activeFilterColumn === col.key ? filterDropdownRef : null}
                            style={getFilterWrapperStyle(activeFilterColumn === col.key)}
                          >
                            <button
                              onClick={(e) => toggleFilterPopup(col.key, e)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none text-center font-light flex items-center justify-between ${
                                (col.key==='RevisedRevenueBudget'
                                  ? (amountRange.min!==null||amountRange.max!==null)
                                  : (billingProgressRange.min!==null||billingProgressRange.max!==null)
                                ) ? 'text-red-600' : 'text-zinc-500'
                              }`}
                            >
                              <span>Filter</span>
                              <FiFilter className="ml-2" />
                            </button>
                            {activeFilterColumn === col.key &&
                              renderFilterPopup(
                                <RangeFilterDropdown
                                  isOpen={activeFilterColumn === col.key}
                                  currentRange={col.key==='RevisedRevenueBudget' ? amountRange : billingProgressRange}
                                  onApply={col.key==='RevisedRevenueBudget' ? setAmountRange : setBillingProgressRange}
                                  onClose={closeFilterPopup}
                                />
                              )}
                          </div>
                        ) : col.isDateFilter ? (
                          <div
                            className="relative"
                            ref={activeFilterColumn === col.key ? filterDropdownRef : null}
                            style={getFilterWrapperStyle(activeFilterColumn === col.key)}
                          >
                            <button
                              onClick={(e) => toggleFilterPopup(col.key, e)}
                              className={`w-full px-2 py-1 text-sm border rounded focus:outline-none text-center font-light flex items-center justify-between ${
                                (col.key === 'created_at'
                                  ? (dateRange.start||dateRange.end)
                                  : (lastOnSiteRange.start||lastOnSiteRange.end)
                                ) ? 'text-red-600' : 'text-zinc-500'
                              }`}
                            >
                              <span className="truncate">
                                {col.key === 'created_at'
                                  ? (dateRange.start||dateRange.end
                                      ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
                                      : 'Filter')
                                  : (lastOnSiteRange.start||lastOnSiteRange.end
                                      ? `${formatDate(lastOnSiteRange.start)} - ${formatDate(lastOnSiteRange.end)}`
                                      : 'Filter')
                                }
                              </span>
                              <FiFilter className="ml-2" />
                            </button>
                            {activeFilterColumn === col.key &&
                              renderFilterPopup(
                                <DateRangeFilterDropdown
                                  isOpen={activeFilterColumn === col.key}
                                  currentRange={col.key === 'created_at' ? dateRange : lastOnSiteRange}
                                  onApply={col.key === 'created_at' ? setDateRange : setLastOnSiteRange}
                                  onClose={closeFilterPopup}
                                />
                              )}
                          </div>
                        ) : (col.key === 'Project__r.Project_System_Wide_ID__c'
                          || col.key === 'Project__r.Name'
                          || col.key === 'Opportunity_System_Wide_ID__c'
                          || col.key === 'Contract_With__r.Name'
                          || col.key === 'Scope_Description__c'
                        ) ? (
                          <input
                            type="text"
                            placeholder="Search"
                            className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                            value={columnFilters[col.key] || ''}
                            onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                          />
                        ) : null}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : filteredAndSorted.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-gray-500 text-center" colSpan={visibleTableColumns.length}>
                  No archived projects to display
                </td>
              </tr>
            ) : (
              filteredAndSorted.map((row) => (
                <tr key={row.Id} className="hover:bg-gray-50">
                  {visibleTableColumns.map((col) => {
                    const isPinned = pinnedVisibleKeys.includes(col.key);
                    const pinnedClass = `${isPinned ? ' projects-col-pinned' : ''}${col.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`;

                    if (col.key === 'Contact_Archived') {
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-4 text-sm text-gray-900 text-center max-md:whitespace-normal break-words${pinnedClass}`}
                          style={getColumnStyle(col)}
                        >
                          <button
                            onClick={() => handleArchivedContactsClick(row)}
                            className="text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                          >
                            Click here
                          </button>
                        </td>
                      );
                    }

                    if (col.key === 'Email_Archived') {
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-4 text-sm text-gray-900 text-center max-md:whitespace-normal break-words${pinnedClass}`}
                          style={getColumnStyle(col)}
                        >
                          <button
                            onClick={() => handleArchivedEmailsClick(row)}
                            className="text-red-600 hover:text-red-800 hover:underline cursor-pointer"
                          >
                            Click here
                          </button>
                        </td>
                      );
                    }

                    const raw = getByKey(row, col.key);
                    const value = col.key === 'RevisedRevenueBudget'
                      ? formatCurrency(raw)
                      : col.key === 'BillingProgress'
                        ? (raw ? `${raw}%` : '-')
                        : col.key === 'created_at' || col.key === 'next_onsite'
                          ? formatDate(raw)
                          : (raw ?? '-');

                    if (col.key === 'Scope_Description__c') {
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-4 text-sm text-gray-900 truncate max-md:whitespace-normal break-words${pinnedClass}`}
                          style={getColumnStyle(col)}
                          title={value}
                        >
                          <button
                            type="button"
                            className="text-left w-full cursor-pointer truncate text-gray-900 max-md:whitespace-normal"
                            onClick={() => {
                              setScopeDescriptionText(value === '-' ? '' : (value || ''));
                              setScopeDescriptionTitle(row.Project__r?.Name || 'Scope Description');
                              setIsScopeDescriptionOpen(true);
                            }}
                          >
                            {value}
                          </button>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={col.key}
                        className={`px-4 py-4 text-sm text-gray-900 truncate max-md:whitespace-normal break-words${pinnedClass}`}
                        style={getColumnStyle(col)}
                        title={value}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
            {!loading && filteredAndSorted.length > 0 && (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    <span className="text-gray-500">No more data to load</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}

       {/* Archived Contacts Modal */}
      {isArchivedContactsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-[96rem] max-w-[90vw] max-h-[90vh] relative">
            <div className="flex justify-between items-center px-5 py-4 border-b">
              <div className="text-xl font-semibold">
                Archived Contacts - {selectedProjectForArchivedContacts?.Project__r?.Name || 'Project'}
              </div>
              <button
                onClick={() => setIsArchivedContactsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto max-h-[calc(90vh-80px)] [&::-webkit-scrollbar]:w-2 
              [&::-webkit-scrollbar-track]:bg-gray-100 
              [&::-webkit-scrollbar-thumb]:bg-gray-300
              [&::-webkit-scrollbar-thumb]:rounded-full 
              [&::-webkit-scrollbar-track]:rounded-full">
              {loadingArchivedContacts ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
                  <span className="ml-2 text-gray-600">Loading archived contacts...</span>
                </div>
              ) : archivedContactsData.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No archived contacts found.</div>
              ) : (() => {
                const transformedSections = transformArchivedContactsData(archivedContactsData);
                return transformedSections ? (
                  <>
                    {Object.entries(transformedSections).map(([key, section]) => (
                      <div key={key}>
                        <ContactSection
                          title={section.title}
                          sections={section.contacts}
                          sectionType={key}
                          hideUpdate={true}
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center text-gray-500 py-8">No archived contacts found.</div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Archived Emails Modal - Email List View */}
      {isArchivedEmailsModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <button
              onClick={() => setIsArchivedEmailsModalOpen(false)}
              className="absolute top-3 right-4 text-gray-600 hover:text-black text-2xl font-bold z-10"
              aria-label="Close email preview"
            >
              &times;
            </button>

            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">
                Archived Emails - {selectedProjectForArchivedEmails?.Project__r?.Name || 'Project'}
              </h2>
            </div>

            <div className="px-6 pt-4 pb-2 overflow-y-auto max-h-[70vh] space-y-3 text-sm text-gray-700">
              {loadingArchivedEmails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
                  <span className="ml-2 text-gray-600">Loading archived emails...</span>
                </div>
              ) : archivedEmailsData.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No archived emails for this project.</p>
              ) : (() => {
                const transformedEmails = transformArchivedEmailsData(archivedEmailsData);
                return transformedEmails.map((email) => (
                  <div
                    key={email.id}
                    className="bg-white rounded-lg shadow-sm p-3 mb-2 border border-gray-200 hover:shadow-md transition cursor-pointer"
                    onClick={() => setSelectedEmailForArchivedEmails(email)}
                  >
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <span title={formatDateTimeForEmail(email.time).fullDateTime}>
                        {formatDateForEmail(email.time)}
                      </span>
                      <span>•</span>
                      <span>{email.folder}</span>
                    </div>
                    <h3 className="text-sm font-medium text-gray-800 mt-1 truncate">
                      {email.subject}
                    </h3>
                    <p className="text-xs text-gray-600 truncate">
                      From: {email.from} &lt;{email.fromEmail}&gt;
                    </p>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Archived Emails Modal - Email Detail View */}
      {selectedEmailForArchivedEmails && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-hidden">
            <button
              onClick={() => setSelectedEmailForArchivedEmails(null)}
              className="absolute top-3 right-4 text-gray-600 hover:text-black text-2xl font-bold z-10"
              aria-label="Close email preview"
            >
              &times;
            </button>

            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{selectedEmailForArchivedEmails.subject}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {formatDateTimeForEmail(selectedEmailForArchivedEmails.time).fullDateTime}
                <span className="ml-1 text-gray-400">
                  ({formatDateTimeForEmail(selectedEmailForArchivedEmails.time).timeZone})
                </span>
              </p>
            </div>

            <div className="px-6 pt-4 pb-2 overflow-y-auto max-h-[70vh] space-y-3 text-sm text-gray-700">
              <div><strong>From:</strong> {selectedEmailForArchivedEmails.from} &lt;{selectedEmailForArchivedEmails.fromEmail}&gt;</div>
              <div><strong>To:</strong> {selectedEmailForArchivedEmails.to.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              {selectedEmailForArchivedEmails.cc.length > 0 && (
                <div><strong>CC:</strong> {selectedEmailForArchivedEmails.cc.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              )}
              {selectedEmailForArchivedEmails.bcc.length > 0 && (
                <div><strong>BCC:</strong> {selectedEmailForArchivedEmails.bcc.map(r => `${r.name} <${r.email}>`).join(", ")}</div>
              )}
              <hr className="my-3 border-gray-300" />
              <div
                className="email-body prose max-w-none"
                dangerouslySetInnerHTML={{ __html: selectedEmailForArchivedEmails.body }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Scope Description Modal */}
      {isScopeDescriptionOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <button
              onClick={() => setIsScopeDescriptionOpen(false)}
              className="absolute top-3 right-4 text-gray-600 hover:text-black text-2xl font-bold z-10"
              aria-label="Close scope description"
            >
              &times;
            </button>
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{scopeDescriptionTitle}</h2>
              <p className="text-sm text-gray-500 mt-1">Scope Description</p>
            </div>
            <div className="px-6 pt-4 pb-6 overflow-y-auto max-h-[65vh] text-sm text-gray-800 whitespace-pre-wrap">
              {scopeDescriptionText && scopeDescriptionText !== '-' ? scopeDescriptionText : 'No scope description available.'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectArchive;
